// Lightweight analytics layer.
// - If VITE_POSTHOG_KEY is set, sends events to PostHog (free up to 1M events/month).
// - Otherwise falls back to console.log so events are still visible during dev.
// All calls are no-ops on the server (SSR safe).

import posthog from "posthog-js";

let initialized = false;
let enabled = false;

function init() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com";
  if (key) {
    try {
      posthog.init(key, {
        api_host: host,
        capture_pageview: true,
        capture_pageleave: true,
        person_profiles: "identified_only",
      });
      enabled = true;
    } catch (e) {
      console.warn("PostHog init failed", e);
    }
  }
}

export type AnalyticsEvent =
  | "session_started"
  | "itinerary_generated"
  | "account_created"
  | "paywall_view"
  | "checkout_started"
  | "checkout_completed"
  | "credits_purchased"
  | "quota_limit_hit"
  | "login_started"
  | "calendar_exported"
  | "pdf_exported";

export function track(event: AnalyticsEvent, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  init();
  if (enabled) {
    try { posthog.capture(event, props); } catch (e) { /* swallow */ }
  } else {
    // Dev fallback so the funnel is still measurable in console.
    console.info(`[analytics] ${event}`, props ?? {});
  }
}

export function identify(userId: string, traits?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  init();
  if (enabled) {
    try { posthog.identify(userId, traits); } catch (e) { /* swallow */ }
  } else {
    console.info(`[analytics] identify`, userId, traits ?? {});
  }
}

export function resetAnalytics() {
  if (typeof window === "undefined" || !enabled) return;
  try { posthog.reset(); } catch (e) { /* swallow */ }
}
